import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { Box, ConfirmationModal, TextField } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useOverlayContext, useStore } from '~/contexts';
import { useCreateCustomPageTemplate } from '~/hooks/reactQuery/mutations/useCreateCustomPageTemplate';
import { useUpdateCustomPageTemplate } from '~/hooks/reactQuery/mutations/useUpdateCustomPageTemplate';
import { useCustomPageText } from '~/hooks/reactQuery/queries/useCustomPageText';
import { useLocaleKeys } from '~/i18n';
import { selectors } from '~/store/state-selectors';
import { extractExistingTemplateId, isDuplicateNameError } from '~/utils/api/isDuplicateNameError';
import { DuplicateNameModal } from './DuplicateNameModal/DuplicateNameModal';
export const GetSaveAsTemplateModalTID = (parentTestId)=>getTestIds(parentTestId);
const buildTypeSpecificPayload = (page, textContent)=>{
    switch(page.type){
        case 'text':
            return {
                type: 'text',
                text: {
                    content: textContent ? JSON.stringify(textContent) : ''
                }
            };
        case 'pdf':
            return {
                type: 'pdf',
                pdf: page.pdf
            };
        case 'video':
            return {
                type: 'video',
                video: page.video
            };
    }
};
export const SaveAsTemplateModal = (param)=>{
    let { page, sourceId, dismissModal, onSaved } = param;
    const { t } = useLocaleKeys();
    const testIds = useMemo(()=>GetSaveAsTemplateModalTID(), []);
    const businessId = useStore(selectors.user.businessId);
    const { showModal } = useOverlayContext();
    const [name, setName] = useState('');
    const [nameError, setNameError] = useState(null);
    const textId = page.type === 'text' ? page.text.textId : undefined;
    const { content: loadedTextContent, isLoading: isLoadingText } = useCustomPageText({
        sourceId,
        textId
    });
    const { createTemplate, isLoading: isCreating } = useCreateCustomPageTemplate({
        onSuccess: ()=>{
            dismissModal();
            onSaved === null || onSaved === void 0 ? void 0 : onSaved();
        }
    });
    const { updateTemplate, isLoading: isUpdating } = useUpdateCustomPageTemplate();
    const buildPayload = useCallback((templateName)=>{
        const typePayload = buildTypeSpecificPayload(page, loadedTextContent);
        return _object_spread({
            name: templateName,
            pageTitle: page.pageTitle,
            displayScope: page.displayScope
        }, typePayload);
    }, [
        page,
        loadedTextContent
    ]);
    const handleOverride = useCallback((templateId, templateName, dismissDuplicate)=>{
        const data = buildPayload(templateName);
        updateTemplate({
            businessId: businessId !== null && businessId !== void 0 ? businessId : '',
            templateId,
            data
        }, {
            onSuccess: ()=>{
                dismissDuplicate();
                dismissModal();
                onSaved === null || onSaved === void 0 ? void 0 : onSaved();
            }
        });
    }, [
        buildPayload,
        businessId,
        dismissModal,
        onSaved,
        updateTemplate
    ]);
    const openDuplicateModal = useCallback((templateName, existingTemplateId)=>{
        showModal({
            biName: 'duplicate_custom_page_template_name_modal',
            content: (param)=>{
                let { dismissModal: dismissDuplicate } = param;
                return /*#__PURE__*/ _jsx(DuplicateNameModal, {
                    existingName: templateName,
                    dismissModal: dismissDuplicate,
                    onEditName: ()=>dismissDuplicate(),
                    onOverride: ()=>handleOverride(existingTemplateId, templateName, dismissDuplicate)
                });
            }
        });
    }, [
        showModal,
        handleOverride
    ]);
    const handleSave = useCallback(()=>{
        const trimmedName = name.trim();
        if (trimmedName === '') {
            setNameError(t.customPages.templates.fieldRequired());
            return;
        }
        if (page.type === 'text' && loadedTextContent == null) {
            return;
        }
        const data = buildPayload(trimmedName);
        createTemplate({
            businessId: businessId !== null && businessId !== void 0 ? businessId : '',
            data
        }, {
            onError: (error)=>{
                if (isDuplicateNameError(error)) {
                    const existingId = extractExistingTemplateId(error);
                    if (existingId != null) {
                        openDuplicateModal(trimmedName, existingId);
                    }
                }
            }
        });
    }, [
        name,
        page.type,
        loadedTextContent,
        buildPayload,
        businessId,
        createTemplate,
        openDuplicateModal,
        t
    ]);
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        bi: {
            primaryButton: {
                name: 'save_as_template_confirm'
            },
            secondaryButton: {
                name: 'save_as_template_cancel'
            }
        },
        title: t.customPages.saveAsTemplateModal.title(),
        testId: testIds.SaveAsTemplateModalTestIds.Root(),
        onCloseButtonClick: dismissModal,
        width: "650px",
        primaryButtonLabel: t.customPages.templates.save(),
        primaryButtonTestId: testIds.SaveAsTemplateModalTestIds.SaveButton(),
        primaryButtonLoading: isCreating || isUpdating || isLoadingText,
        onPrimaryButtonClick: handleSave,
        secondaryButtonLabel: t.customPages.templates.cancel(),
        secondaryButtonTestId: testIds.SaveAsTemplateModalTestIds.CancelButton(),
        onSecondaryButtonClick: dismissModal,
        content: /*#__PURE__*/ _jsx(Box, {
            style: {
                minHeight: 70
            },
            children: /*#__PURE__*/ _jsx(TextField, {
                bi: {
                    name: 'custom_page_template_name'
                },
                testId: testIds.SaveAsTemplateModalTestIds.NameField(),
                label: t.customPages.saveAsTemplateModal.templateNameLabel(),
                value: name,
                onChange: (e)=>{
                    setName(e.target.value);
                    setNameError(null);
                },
                error: nameError != null,
                helperText: nameError !== null && nameError !== void 0 ? nameError : undefined
            })
        })
    });
};
